import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { signIn, confirmSignIn, signOut, signInWithRedirect, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { amplifyConfig } from '../amplify-config';

export interface SignInResult {
  isSignedIn: boolean;
  /** True when Cognito requires the user to set a new password (e.g. first login with temporary password). */
  requiresNewPassword?: boolean;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private configured = false;

  private ensureConfigured(): void {
    if (!this.configured) {
      Amplify.configure(amplifyConfig);
      this.configured = true;
    }
  }

  async signIn(username: string, password: string): Promise<SignInResult> {
    try {
      this.ensureConfigured();
      try {
        await signOut();
      } catch {
        // No existing user; ignore
      }
      const result = await signIn({ username, password });
      const step = result.nextStep?.signInStep;
      if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { isSignedIn: false, requiresNewPassword: true };
      }
      if (step === 'DONE') {
        return { isSignedIn: true };
      }
      return { isSignedIn: false, error: `Unsupported sign-in step: ${step}` };
    } catch (e: unknown) {
      const err = e as { message?: string };
      return { isSignedIn: false, error: err?.message ?? 'Sign-in failed' };
    }
  }

  /** Complete sign-in when Cognito challenged with NEW_PASSWORD_REQUIRED. Call after user submits new password. */
  async confirmSignInWithNewPassword(newPassword: string): Promise<SignInResult> {
    try {
      this.ensureConfigured();
      const result = await confirmSignIn({ challengeResponse: newPassword });
      const step = result.nextStep?.signInStep;
      if (step === 'DONE') {
        return { isSignedIn: true };
      }
      return { isSignedIn: false, error: `Unexpected step after password change: ${step}` };
    } catch (e: unknown) {
      const err = e as { message?: string };
      return { isSignedIn: false, error: err?.message ?? 'Password change failed' };
    }
  }

  async signOut(): Promise<void> {
    this.ensureConfigured();
    await signOut();
  }

  /** Redirect to Cognito Hosted UI for OAuth login. Used when user comes from institucional. */
  async signInWithRedirect(): Promise<void> {
    this.ensureConfigured();
    await signInWithRedirect();
  }

  /** Clear auth state when tokens reference a deleted Cognito client (e.g. after pool recreate). */
  private async clearIfStaleClient(e: unknown): Promise<void> {
    const msg = String((e as { message?: string })?.message ?? '');
    if (msg.includes('does not exist') || msg.includes('User pool client')) {
      try {
        await signOut();
      } catch {
        // Ignore signOut errors
      }
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      this.ensureConfigured();
      await getCurrentUser();
      const s = await fetchAuthSession();
      return !!s?.tokens?.idToken;
    } catch (e) {
      await this.clearIfStaleClient(e);
      return false;
    }
  }

  async getIdToken(): Promise<string | null> {
    try {
      this.ensureConfigured();
      const s = await fetchAuthSession();
      return s.tokens?.idToken?.toString() ?? null;
    } catch (e) {
      await this.clearIfStaleClient(e);
      return null;
    }
  }
}
